import { useState, useEffect, useId, useRef, cloneElement, isValidElement, Children } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react';

// NOTE: <ToastContainer /> is mounted once at the top of <App /> and hosts both
// toasts AND confirm()/prompt() dialogs — no extra <UIRoot /> mount is needed.

// ─── Badge ───
// Text color = color, background = 12% tinted color over transparent.
// Fix: antes la concatenación .replace() producía CSS inválido
// ("color-mix(in srgb, --accent,0.12)" sin var()) → fondo transparente,
// contraste pobre en varias páginas. Ahora color-mix bien formado.
export function Badge({ children, color = 'var(--accent)', style }) {
  return (
    <span
      className="badge"
      style={{
        color,
        backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
        border: `1px solid color-mix(in srgb, ${color} 25%, transparent)`,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// Luminance helper for contrast-aware text on colored backgrounds.
// Returns true si el color es oscuro (texto blanco es legible encima).
function isDarkColor(hexOrVar) {
  // Si es var() no podemos calcular → asumimos oscuro (white text ok para accent)
  if (typeof hexOrVar !== 'string' || !hexOrVar.startsWith('#')) return true;
  const hex = hexOrVar.slice(1);
  const n = hex.length === 3
    ? [hex[0]+hex[0], hex[1]+hex[1], hex[2]+hex[2]]
    : [hex.slice(0,2), hex.slice(2,4), hex.slice(4,6)];
  const [r, g, b] = n.map(h => parseInt(h, 16));
  // Relative luminance (simplificado)
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L < 0.65;
}

// Solid badge — texto blanco en colores oscuros, texto oscuro en claros (contraste auto).
export function SolidBadge({ children, color = '#6B7280', style }) {
  const textColor = isDarkColor(color) ? '#fff' : '#111';
  return (
    <span className="badge" style={{ color: textColor, backgroundColor: color, ...style }}>
      {children}
    </span>
  );
}

// Labels humanos de módulos (usados en enabled_modules, plans.modules, etc.)
// Centralizado aquí para que toda la app muestre lo mismo.
export const MODULE_LABELS = {
  events:              'Gestión de Eventos',
  client_portal:       'Portal de Clientes',
  pro_portal:          'Portal Profesionales',
  billing_adv:         'Facturación Avanzada',
  custom_rates:        'Tarifas Personalizadas',
  notifications:       'Notificaciones',
  ambulance_live:      'Ambulancia Live',
  ai_auto_assign:      'Auto-asignación IA',
  pro_documents:       'Documentación Profesional',
  // Módulos nuevos (B1-B9):
  maintenance:         'Mantenimiento de Flota',
  shifts:              'Turnos y Fichaje',
  medical_stock:       'Inventario Médico',
  patient_records:     'Partes Clínicos (PCR)',
  certifications:      'Certificaciones',
  leads:               'CRM Pipeline',
  quality:             'Calidad / ISO 9001',
  analytics:           'Vista Ejecutiva / BI',
};

export const moduleLabel = (key) => MODULE_LABELS[key] || key;

const STATUS_MAP = {
  active: ['#10B981', 'Activo'], trial: ['#3B82F6', 'Trial'], suspended: ['#EF4444', 'Suspendido'],
  cancelled: ['#6B7280', 'Cancelado'], 
  requested: ['#D97706', 'Solicitada'], pending_quote: ['#EA580C', 'Pte. Presupuesto'],
  quoted: ['#2563EB', 'Presupuestada'], accepted: ['#7C3AED', 'Aceptada'],
  pending_payment: ['#DB2777', 'Pte. Pago'], active_service: ['#059669', 'En servicio'],
  completed: ['#4B5563', 'Finalizada'],
  pending: ['#D97706', 'Pendiente'], covered: ['#059669', 'Cubierta'],
  rejected: ['#DC2626', 'Rechazado'], 
  in_progress: ['#2563EB', 'En curso'], paid: ['#059669', 'Pagado'],
  overdue: ['#DC2626', 'Vencida'], sent: ['#2563EB', 'Enviada'], draft: ['#6B7280', 'Borrador'], partial: ['#D97706', 'Pago Parcial'],
  available: ['#059669', 'Disponible'], on_service: ['#2563EB', 'En servicio'], off: ['#6B7280', 'No disponible'],
  pending_accept: ['#D97706', 'Pendiente'], confirmed: ['#059669', 'Confirmado'],
  approved: ['#059669', 'Aprobada'], expired: ['#6B7280', 'Expirado'], archived: ['#9CA3AF', 'Archivada'], annulled: ['#9CA3AF', 'Anulado'],
};

export function StatusBadge({ status }) {
  const [color, label] = STATUS_MAP[status] || ['#6B7280', status];
  return <SolidBadge color={color}>{label}</SolidBadge>;
}

// ─── Card ───
export function Card({ children, className = '', onClick, style }) {
  return (
    <div className={`card ${onClick ? 'clickable' : ''} ${className} fade-in`} onClick={onClick} style={style}>
      {children}
    </div>
  );
}

// ─── MetricCard ───
export function MetricCard({ icon: IconComp, label, value, sub, color = 'var(--accent)' }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: `color-mix(in srgb, ${color} 15%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IconComp size={20} color={color} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

// ─── Button ───
export function Btn({ children, variant = 'primary', size = 'md', icon: IconComp, onClick, disabled, style, type = 'button', title, 'aria-label': ariaLabel }) {
  return (
    <button
      type={type}
      className={`btn ${variant} ${size}`}
      onClick={onClick}
      disabled={disabled}
      style={style}
      title={title}
      aria-label={ariaLabel || (typeof children === 'string' ? undefined : title)}
    >
      {IconComp && <IconComp size={size === 'sm' ? 14 : 16} />}
      {children}
    </button>
  );
}

// ─── Input ───
// Auto-wires `htmlFor` → first child input id via useId(), so screen readers
// associate the label with the control. Callers can still override by passing
// `id` on the child explicitly.
export function FormField({ label, children, style, id: idProp, required, error, hint, requiredMark = '*' }) {
  const autoId = useId();
  const id = idProp || autoId;

  // Only the first valid element child receives the id; extra children pass through.
  // Si required=true y el hijo es input/select/textarea, propagamos required + aria-invalid
  // para que la validación nativa del browser dispare al submit.
  const kids = Children.toArray(children);
  let injected = false;
  const mapped = kids.map((child) => {
    if (!injected && isValidElement(child)) {
      injected = true;
      const extraProps = { id: child.props.id ?? id };
      if (required && !child.props.required) extraProps.required = true;
      if (error) extraProps['aria-invalid'] = true;
      if (error) extraProps['aria-describedby'] = `${id}-err`;
      else if (hint) extraProps['aria-describedby'] = `${id}-hint`;
      return cloneElement(child, extraProps);
    }
    return child;
  });

  return (
    <div className="form-group" style={style}>
      {label && (
        <label className="form-label" htmlFor={id}>
          {label}
          {required && <span style={{ color: '#EF4444', marginLeft: 4 }} aria-hidden="true">{requiredMark}</span>}
        </label>
      )}
      {mapped}
      {error && (
        <div id={`${id}-err`} role="alert" style={{ color: '#EF4444', fontSize: 11, marginTop: 4, fontWeight: 600 }}>
          {error}
        </div>
      )}
      {!error && hint && (
        <div id={`${id}-hint`} style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

// ─── Modal ───
const MODAL_SIZES = { sm: 480, md: 720, lg: 960, xl: 1200 };
const FOCUSABLE_SELECTOR = '[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, subtitle, icon: Icon, children, width, size = 'md', footer }) {
  const resolvedWidth = width || MODAL_SIZES[size] || MODAL_SIZES.md;
  const contentRef = useRef(null);

  // Keyboard: Escape closes modal
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock body scroll while modal open (prevents background scrolling on mobile)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Focus trap: move focus into modal, cycle on Tab/Shift+Tab, restore on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const node = contentRef.current;

    const getFocusable = () => {
      if (!node) return [];
      return Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    };

    // Defer one tick so children are rendered before focusing.
    const focusTimer = setTimeout(() => {
      const focusables = getFocusable();
      if (focusables.length > 0) focusables[0].focus();
      else if (node) { node.setAttribute('tabindex', '-1'); node.focus(); }
    }, 0);

    const handleKey = (e) => {
      if (e.key !== 'Tab') return;
      const focusables = getFocusable();
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !node.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !node.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKey);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch (_) { /* node might have unmounted */ }
      }
    };
  }, [open]);

  if (!open) return null;
  // Portal to document.body so the fixed overlay escapes any ancestor stacking
  // context (transform/filter/animation on parents would otherwise clip it).
  return createPortal(
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
      <div ref={contentRef} className="modal-content fade-in" onClick={e => e.stopPropagation()} style={{ width: `min(${resolvedWidth}px, 92vw)` }}>
        <header className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
            {Icon && <div className="modal-header-icon"><Icon size={18} /></div>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 className="modal-title">{title}</h3>
              {subtitle && <p className="modal-subtitle">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="modal-close-btn"><X size={20} /></button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}

// ─── EmptyState (reusable, consistent empty placeholders) ───
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="mc-empty-state">
      {Icon && <div className="mc-empty-state-icon"><Icon size={28} /></div>}
      {title && <div className="mc-empty-state-title">{title}</div>}
      {description && <div className="mc-empty-state-desc">{description}</div>}
      {action && <div style={{ marginTop: 4 }}>{action}</div>}
    </div>
  );
}

// ─── DataTable ───
export function DataTable({ columns, data, onRowClick, emptyText = 'Sin datos' }) {
  if (!data || data.length === 0) {
    return <div className="card"><EmptyState title={emptyText} /></div>;
  }
  return (
    <div style={{ overflowX: 'auto', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
      <table className="data-table">
        <thead>
          <tr>{columns.map((c, i) => <th key={i}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr key={ri} onClick={() => onRowClick?.(row)} style={{ cursor: onRowClick ? 'pointer' : 'default' }}>
              {columns.map((c, ci) => <td key={ci}>{c.render ? c.render(row) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── TabBar ───
export function TabBar({ tabs, active, onChange }) {
  return (
    <div className="tab-bar">
      {tabs.map(t => {
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            className={`tab-item ${active === t.id ? 'active' : ''}`}
            onClick={() => onChange(t.id)}
            style={Icon ? { display: 'inline-flex', alignItems: 'center', gap: 6 } : undefined}
          >
            {Icon && <Icon size={15} style={{ flexShrink: 0 }} />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── SectionHeader ───
export function SectionHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="section-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
        {Icon && <Icon size={22} style={{ color: 'var(--accent)', flexShrink: 0 }} />}
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 className="section-title" style={{ margin: 0 }}>{title}</h2>
          {subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

// ─── Loading ───
export function Loading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <Loader2 size={28} className="spinner" style={{ color: 'var(--accent)' }} />
    </div>
  );
}

// ─── Skeleton loaders (usan .skeleton del design system) ───
export function SkeletonLine({ width = '100%', height = 14, style }) {
  return <div className="skeleton skeleton-text" style={{ width, height, ...style }} />;
}

export function SkeletonCircle({ size = 40, style }) {
  return <div className="skeleton skeleton-circle" style={{ width: size, height: size, ...style }} />;
}

export function SkeletonCard({ lines = 3, hasAvatar = false }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {hasAvatar && <SkeletonCircle size={44} />}
        <div style={{ flex: 1, display: 'grid', gap: 8 }}>
          <SkeletonLine width="60%" height={16} />
          {Array.from({ length: lines }).map((_, i) => (
            <SkeletonLine key={i} width={i === lines - 1 ? '40%' : '90%'} height={12} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function SkeletonMetric() {
  return (
    <div className="card" style={{ padding: 16, display: 'grid', gap: 8 }}>
      <SkeletonLine width={28} height={28} style={{ borderRadius: 8 }} />
      <SkeletonLine width="70%" height={24} />
      <SkeletonLine width="50%" height={11} />
    </div>
  );
}

export function SkeletonGrid({ count = 4, type = 'metric' }) {
  const Component = type === 'metric' ? SkeletonMetric : SkeletonCard;
  return (
    <div className="grid-cols-4" style={{ marginBottom: 20 }}>
      {Array.from({ length: count }).map((_, i) => <Component key={i} />)}
    </div>
  );
}

export function SkeletonList({ count = 5, hasAvatar = true }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {Array.from({ length: count }).map((_, i) => <SkeletonCard key={i} lines={2} hasAvatar={hasAvatar} />)}
    </div>
  );
}

// ─── Global Toast System ───
const TOAST_ICONS = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info };
const TOAST_COLORS = { success: '#059669', error: '#EF4444', warning: '#F59E0B', info: '#3B82F6' };

// Error message translator — converts technical errors to user-friendly
const FRIENDLY_ERRORS = [
  [/column "(\w+)" .* does not exist/i, 'Error de configuración. Contacta con soporte.'],
  [/relation "(\w+)" does not exist/i, 'La base de datos necesita reiniciarse. Contacta con soporte.'],
  [/duplicate key/i, 'Este registro ya existe. Comprueba los datos.'],
  [/violates.*constraint/i, 'No se puede completar porque hay datos relacionados.'],
  [/Ya existe un usuario con ese email/i, 'Ya hay un usuario registrado con ese email.'],
  [/token.*expired|jwt.*expired/i, 'Tu sesión ha caducado. Vuelve a iniciar sesión.'],
  [/not found|no encontrad/i, 'El recurso solicitado no existe o fue eliminado.'],
  [/network|fetch|timeout|ECONNREFUSED/i, 'Error de conexión. Comprueba tu internet.'],
  [/internal|500/i, 'Error del servidor. Inténtalo de nuevo en unos segundos.'],
  [/forbidden|403/i, 'No tienes permisos para esta acción.'],
  [/unauthorized|401/i, 'Sesión no válida. Inicia sesión de nuevo.'],
];

function friendlyError(msg) {
  if (!msg) return 'Ha ocurrido un error inesperado.';
  for (const [regex, friendly] of FRIENDLY_ERRORS) {
    if (regex.test(msg)) return friendly;
  }
  // If it's already user-friendly (Spanish, short), keep it
  if (msg.length < 100 && /^[A-ZÁÉÍÓÚÑ]/.test(msg)) return msg;
  return msg;
}

// Global toast state (singleton)
let _toastListeners = [];
export const toast = {
  _listeners: _toastListeners,
  show(type, title, message, duration = 5000) {
    const t = { id: Date.now(), type, title, message, duration };
    _toastListeners.forEach(fn => fn(t));
  },
  success(title, message) { this.show('success', title, message); },
  error(title, message) { this.show('error', title || 'Error', friendlyError(message)); },
  warning(title, message) { this.show('warning', title, message); },
  info(title, message) { this.show('info', title, message); },
  subscribe(fn) { _toastListeners.push(fn); return () => { _toastListeners = _toastListeners.filter(f => f !== fn); }; },
};

// ─── Global confirm()/prompt() dialog bus (singleton) ───
// Piggybacks on <ToastContainer />, no extra mount required.
let _dialogListeners = [];
const _dialogBus = {
  subscribe(fn) { _dialogListeners.push(fn); return () => { _dialogListeners = _dialogListeners.filter(f => f !== fn); }; },
  push(descriptor) { _dialogListeners.forEach(fn => fn(descriptor)); },
};

function openDialog(descriptor) {
  return new Promise((resolve) => {
    _dialogBus.push({ ...descriptor, id: Date.now() + Math.random(), resolve });
  });
}

export const confirm = ({ title = 'Confirmar', message = '', confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', variant = 'primary' } = {}) =>
  openDialog({ kind: 'confirm', title, message, confirmLabel, cancelLabel, variant });

export const prompt = ({ title = 'Introducir valor', message = '', placeholder = '', initialValue = '', confirmLabel = 'Aceptar', cancelLabel = 'Cancelar' } = {}) =>
  openDialog({ kind: 'prompt', title, message, placeholder, initialValue, confirmLabel, cancelLabel });

function DialogHost() {
  const [dialog, setDialog] = useState(null);
  const [value, setValue] = useState('');

  useEffect(() => {
    const unsub = _dialogBus.subscribe((d) => {
      setDialog(d);
      setValue(d.kind === 'prompt' ? (d.initialValue || '') : '');
    });
    return unsub;
  }, []);

  if (!dialog) return null;

  const close = (result) => {
    dialog.resolve(result);
    setDialog(null);
  };
  const handleCancel = () => close(dialog.kind === 'prompt' ? null : false);
  const handleConfirm = () => close(dialog.kind === 'prompt' ? value : true);

  const variantClass = dialog.variant === 'danger' ? 'danger' : (dialog.variant || 'primary');

  return (
    <Modal
      open={true}
      onClose={handleCancel}
      title={dialog.title}
      size="sm"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" onClick={handleCancel}>{dialog.cancelLabel}</Btn>
          <Btn variant={variantClass} onClick={handleConfirm}>{dialog.confirmLabel}</Btn>
        </div>
      }
    >
      {dialog.message && <p style={{ margin: '0 0 12px 0', color: 'var(--text-muted)', fontSize: 14 }}>{dialog.message}</p>}
      {dialog.kind === 'prompt' && (
        <input
          type="text"
          className="form-input"
          autoFocus
          value={value}
          placeholder={dialog.placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--input-bg, var(--card))', color: 'var(--text)' }}
        />
      )}
    </Modal>
  );
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsub = toast.subscribe((t) => {
      setToasts(prev => [...prev, t]);
      setTimeout(() => setToasts(prev => prev.filter(x => x.id !== t.id)), t.duration || 5000);
    });
    return unsub;
  }, []);

  return (
    <>
      {/* aria-live region: screen readers announce new toasts politely */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380, pointerEvents: toasts.length === 0 ? 'none' : 'auto' }}
      >
        {toasts.map(t => (
          <div key={t.id} style={{
            padding: '12px 16px', borderRadius: 12,
            background: 'var(--card)', border: `2px solid ${TOAST_COLORS[t.type]}30`,
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            animation: 'slideIn 0.3s ease-out',
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            {(() => { const ToastIcon = TOAST_ICONS[t.type] || Info; return <ToastIcon size={20} style={{ color: TOAST_COLORS[t.type], flexShrink: 0 }} />; })()}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: TOAST_COLORS[t.type] }}>{t.title}</div>
              {t.message && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{t.message}</div>}
            </div>
            <button onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
          </div>
        ))}
        <style>{`@keyframes slideIn { from { transform: translateX(100px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
      </div>
      {/* confirm()/prompt() host rides on the same provider */}
      <DialogHost />
    </>
  );
}

// UIRoot: alias of ToastContainer for callers who prefer the explicit name.
// Since ToastContainer is already mounted globally, no extra mount needed.
export const UIRoot = ToastContainer;
